import {
  BackendCoursePrice,
  Course,
  CourseLineSell,
  CoursePaymentPayload,
  CourseWebapp,
  LiffData,
} from '@/type/Course'
import axios from 'axios'
import { useAppStore } from '@/stores/AppStore'
import { PaymentLink, paymentMethod } from '@/type/Payment'
import { CourseCheckoutPricing } from '@/utils/courseCheckoutPricing'
import { WEB_APP_URL } from '@/stores/Constant'

interface RawCourseLineSell extends Omit<CourseLineSell, 'liff_data'> {
  liff_data: LiffData
}

const formatSellCourse = (sell_course: RawCourseLineSell[]): CourseLineSell[] => {
  return sell_course.map(course => {
    const course_images = course.liff_data.images.map((img_id: number) => {
      return course.liff_data_images[img_id]
    })
    course_images.sort((a, b) => a.order - b.order)

    return {
      ...course,
      liff_data: {
        ...course.liff_data,
        images: course_images,
      },
    }
  })
}

export function useCourseRepository(clinic_uuid: string) {
  const appStore = useAppStore()

  async function fetchCourse(course_uuid: string): Promise<Course> {
    try {
      const response = await axios.get(
        `${appStore.url}/api/v1/crm/line-app/p/patient-course/${course_uuid}/?c=${clinic_uuid}&l=${appStore.userProfile.userId}`,
      )

      if (response.data.code) {
        throw new Error(response.data.msg)
      }

      return response.data
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async function fetchSellCourseList(): Promise<CourseLineSell[]> {
    try {
      const response = await axios.get(
        `${appStore.url}/api/v1/crm/line-app/p/sell-course/list/?c=${appStore.clinicUUID}`,
        {
          params: {
            patient_uuid: appStore.patient?.uuid,
          },
        },
      )
      return formatSellCourse(response.data)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async function createPaymentLink(
    pricing: CourseCheckoutPricing,
    payment_method: paymentMethod,
  ): Promise<PaymentLink> {
    if (!appStore.clinic) {
      throw new Error('no clinic data')
    }

    if (!appStore.patient) {
      throw new Error('no patient data')
    }

    if (!appStore.channelId) {
      throw new Error('no channelId data')
    }

    const courses: BackendCoursePrice[] = pricing.rows.map(row => ({
      course_uuid: row.course_uuid,
      remark: null,
      debt: row.debt,
      disc_price: row.disc_price,
      price: row.price,
      paid_amount: row.paid_amount,
      payment_discounts: row.discount_lines,
      vat: row.vat,
      fee: row.fee,
    }))

    const payload: CoursePaymentPayload = {
      channel_id: appStore.channelId,
      patient: { ...appStore.patient },
      payment: {
        courses,
        vat: pricing.vatTotal,
        vat_rate: appStore.clinic.course_VAT_rate,
        vat_type: appStore.clinic.course_VAT_type || null, // "" -> null (คลินิกไม่มี VAT)
        fee: pricing.feeTotal,
        fee_rate: appStore.clinic.course_fee_rate,
        fee_type: appStore.clinic.course_fee_type || null, // "" -> null (คลินิกไม่มี fee)
        total: pricing.total,
        paid_amount: pricing.paidAmount,
        payment_method: payment_method,
        voucher_codes: pricing.appliedVoucherCodes,
      },
    }

    try {
      const response = await axios.post(
        `${appStore.url}/api/v1/crm/line-app/p/course-payment-link/?c=` + appStore.clinic.uuid,
        payload,
      )
      return { ...response.data, cache_data: payload }
    } catch (error) {
      throw error
    }
  }

  async function checkIsCourseBuyValid(course_uuids: string[]) {
    const response = await axios.post(`${appStore.url}/api/v1/crm/line-app/p/verify/patient/buy-course-quota/`, {
      patient_uuid: appStore.patient?.uuid,
      line_user_id: appStore.patient?.line_user_id,
      course_uuids: course_uuids,
    })

    return response
  }

  async function fetchCouseFromWebapp(user_uuid: string): Promise<CourseWebapp[]> {
    try {
      const { data } = await axios.get(`${WEB_APP_URL}/api/v1/course/customer/patient-course/list/`, {
        params: {
          user_uuid: user_uuid,
          // ชื่อบริการในรายการคอร์ส web app backend resolve ตามภาษาให้แล้ว
          lang: appStore.language,
        },
      })
      return data.map((item: CourseWebapp & { branch_line_crm_name?: string }) => ({
        ...item,
        branch_name: item.branch_line_crm_name ?? item.branch_name,
      }))
    } catch (error) {
      throw error
    }
  }

  return {
    fetchCourse,
    fetchSellCourseList,
    createPaymentLink,
    checkIsCourseBuyValid,
    fetchCouseFromWebapp,
  }
}
